// src/redux/slices/businessHoursSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl } from '../../Utils/serverSelector';
import { apiRequest } from '../../Utils/apiClient';
import { clearJwtToken } from '../../Utils/authStorage';

/* =========================
 * ПРИВАТНЫЕ (через apiRequest + JWT)
 * ========================= */

// === Получить рабочие часы мастера по uid (приватный) ===
export const fetchBusinessHours = createAsyncThunk(
  'businessHours/fetchBusinessHours',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');

      const response = await apiRequest('GET', `/business-hours/${uid}`);

      if (!response?.data) throw new Error('No response data received from server.');
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch { }
      }

      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка при получении рабочих часов';
      console.error(`Fetch Business Hours: ${msg}`);
      return rejectWithValue(error?.response?.data || msg);
    }
  }
);

// === Обновить рабочие часы мастера (приватный) ===
export const updateBusinessHours = createAsyncThunk(
  'businessHours/updateBusinessHours',
  async ({ uid, businessHoursData }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      if (!Array.isArray(businessHoursData) || businessHoursData.length === 0) {
        throw new Error('Нет данных для обновления рабочих часов');
      }

      const results = await Promise.allSettled(
        businessHoursData.map(async (dayData) => {
          const res = await apiRequest('POST', '/business-hours', dayData);
          if (!res?.data) throw new Error('No response data received from server.');
          return res.data;
        })
      );

      const successful = results
        .filter((r) => r.status === 'fulfilled' && r.value)
        .map((r) => r.value);

      results.forEach((r, idx) => {
        if (r.status === 'rejected') {
          const msg =
            r.reason?.response?.data?.message ||
            r.reason?.message ||
            'Unknown error';
          const dayOfWeek =
            businessHoursData?.[idx]?.dayOfWeek ??
            businessHoursData?.[idx]?.day;
          console.warn(`Update Business Hours (Day ${dayOfWeek ?? idx}): ${msg}`);
        }
      });

      if (successful.length === 0) {
        throw new Error('Не удалось обновить рабочие часы');
      }
      return successful;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch { }
      }

      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка при обновлении рабочих часов';
      console.error(`Update Business Hours: ${msg}`);
      return rejectWithValue(error?.response?.data || msg);
    }
  }
);

// === Батч-загрузка часов по списку uids (приватный) ===
export const fetchSalonBusinessHours = createAsyncThunk(
  'businessHours/fetchSalonBusinessHours',
  async (uids = [], { rejectWithValue }) => {
    try {
      const rows = await Promise.all(
        (uids || []).map(async (uid) => {
          const res = await apiRequest('GET', `/business-hours/${uid}`);
          const items = (res?.data || []).map((x) => ({ ...x, uid }));
          return { uid, items };
        })
      );
      return rows;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch { }
      }

      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка при салонной загрузке рабочих часов';
      console.error(`fetchSalonBusinessHours: ${msg}`);
      return rejectWithValue(msg);
    }
  }
);

/* =========================
 * ПУБЛИЧНЫЕ (GET, без JWT)
 * ========================= */

// === Публично: получить рабочие часы мастера по uid ===
export const fetchPublicBusinessHours = createAsyncThunk(
  'businessHours/fetchPublicBusinessHours',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      const url = `${getServerUrl()}/public/business-hours/${uid}`;
      const response = await axios.get(url); // без headers
      if (!response?.data) throw new Error('No response data received from server.');
      return response.data;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка при публичном получении рабочих часов';
      console.error(`fetchPublicBusinessHours: ${msg}`);
      return rejectWithValue(error?.response?.data || msg);
    }
  }
);

// === Публично: батч по списку uids (для салона на сайте) ===
export const fetchPublicSalonBusinessHours = createAsyncThunk(
  'businessHours/fetchPublicSalonBusinessHours',
  async (uids = [], { rejectWithValue }) => {
    try {
      const rows = await Promise.all(
        (uids || []).map(async (uid) => {
          const url = `${getServerUrl()}/public/business-hours/${uid}`;
          const res = await axios.get(url); // без headers
          const items = (res?.data || []).map((x) => ({ ...x, uid }));
          return { uid, items };
        })
      );
      return rows;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка при публичной салонной загрузке рабочих часов';
      console.error(`fetchPublicSalonBusinessHours: ${msg}`);
      return rejectWithValue(msg);
    }
  }
);

/* =========================
 * State
 * ========================= */
const initialState = {
  businessHours: [], // текущий мастер (последняя загрузка — приватная ИЛИ публичная)
  status: 'idle',
  error: null,

  byUid: {},       // { [uid]: Day[] }
  statusByUid: {}, // { [uid]: 'idle'|'loading'|'succeeded'|'failed' }
  errorByUid: {},  // { [uid]: string|null }
};

/* =========================
 * Slice
 * ========================= */
const businessHoursSlice = createSlice({
  name: 'businessHours',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // === приватный fetchBusinessHours ===
      .addCase(fetchBusinessHours.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBusinessHours.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.businessHours = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchBusinessHours.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'error';
      })

      // === приватный updateBusinessHours ===
      .addCase(updateBusinessHours.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateBusinessHours.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.businessHours = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(updateBusinessHours.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'error';
      })

      // === приватный fetchSalonBusinessHours ===
      .addCase(fetchSalonBusinessHours.pending, (state, action) => {
        const uids = action.meta?.arg || [];
        uids.forEach((uid) => {
          const k = String(uid);
          state.statusByUid[k] = 'loading';
          state.errorByUid[k] = null;
        });
      })
      .addCase(fetchSalonBusinessHours.fulfilled, (state, action) => {
        const rows = action.payload || [];
        rows.forEach(({ uid, items }) => {
          const k = String(uid);
          state.byUid[k] = Array.isArray(items)
            ? items.map((x) => ({ ...x, uid: k }))
            : [];
          state.statusByUid[k] = 'succeeded';
          state.errorByUid[k] = null;
        });
      })
      .addCase(fetchSalonBusinessHours.rejected, (state, action) => {
        const uids = action.meta?.arg || [];
        uids.forEach((uid) => {
          const k = String(uid);
          state.statusByUid[k] = 'failed';
          state.errorByUid[k] =
            action.payload || action.error?.message || 'error';
        });
      })

      /* =========================
       * ПУБЛИЧНЫЕ обработчики
       * ========================= */

      // === public fetchPublicBusinessHours ===
      .addCase(fetchPublicBusinessHours.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const uid = String(action.meta?.arg);
        const items = Array.isArray(action.payload) ? action.payload : [];

        state.businessHours = items;

        if (uid) {
          state.byUid[uid] = items.map((x) => ({ ...x, uid }));
          state.statusByUid[uid] = 'succeeded';
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicBusinessHours.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'error';

        const uid = String(action.meta?.arg || '');
        if (uid) {
          state.statusByUid[uid] = 'failed';
          state.errorByUid[uid] =
            action.payload || action.error?.message || 'error';
        }
      })

      // === public fetchPublicSalonBusinessHours ===
      .addCase(fetchPublicSalonBusinessHours.pending, (state, action) => {
        const uids = action.meta?.arg || [];
        uids.forEach((uid) => {
          const k = String(uid);
          state.statusByUid[k] = 'loading';
          state.errorByUid[k] = null;
        });
      })
      .addCase(fetchPublicSalonBusinessHours.fulfilled, (state, action) => {
        const rows = action.payload || [];
        rows.forEach(({ uid, items }) => {
          const k = String(uid);
          state.byUid[k] = Array.isArray(items)
            ? items.map((x) => ({ ...x, uid: k }))
            : [];
          state.statusByUid[k] = 'succeeded';
          state.errorByUid[k] = null;
        });
      })
      .addCase(fetchPublicSalonBusinessHours.rejected, (state, action) => {
        const uids = action.meta?.arg || [];
        uids.forEach((uid) => {
          const k = String(uid);
          state.statusByUid[k] = 'failed';
          state.errorByUid[k] =
            action.payload || action.error?.message || 'error';
        });
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default businessHoursSlice.reducer;

/* =========================
 * Селекторы
 * ========================= */
export const selectBusinessHours = (s) =>
  s.businessHours?.businessHours || [];

export const selectBusinessHoursStatus = (s) =>
  s.businessHours?.status || 'idle';

export const selectBusinessHoursError = (s) =>
  s.businessHours?.error || null;

export const selectBusinessHoursByUid = (s, uid) =>
  s.businessHours?.byUid?.[String(uid)] || [];

export const selectBusinessHoursForSalon = (s, uids = []) =>
  (uids || []).flatMap(
    (uid) => s.businessHours?.byUid?.[String(uid)] || []
  );
